/** The Administration console's information architecture.
 *
 * One module for the whole console, with no React in it, because the same three
 * lists drive four different renderings: the rail on the left, the eight module
 * cards on the overview, the quick-action grid, and — since the navigation
 * audit — the ADMINISTRATION section of the service desk's own sidebar. Keeping
 * them in one place is what stops "Ticket Forms" appearing in the rail and not
 * on the card that claims to list everything under Service Management, or
 * appearing in the admin console and nowhere a Manager would ever find it. Tests
 * pin both down.
 *
 * ## Destinations
 *
 * `to` is the honest part. A handful of these settings genuinely have a screen
 * behind them today, so those entries point at it. Everything else routes to
 * `/agent/administration/<slug>`, which renders the capability state and says
 * exactly what the backend is missing — no invented screen, no fake data.
 * `backed` records which is which so the split stays greppable rather than being
 * inferred from a URL.
 *
 * ## Visibility
 *
 * Every row names the permissions that reveal it, and `visibleAdminNav` is the
 * only thing that decides. Two rules make that sufficient:
 *
 * 1. **Permissions, never roles.** `is_superuser` and Django groups authorise
 *    nothing in this product (see `core.authz`); a row gated on a role name
 *    would also become invisible the moment somebody defines a custom role.
 * 2. **Modules need no second check.** `GET /api/agent/me/` returns *effective*
 *    permissions — already narrowed by module entitlement — so an organisation
 *    with the assets module switched off simply does not hold `asset.edit`, and
 *    the CMDB rows disappear without this file knowing what a licence is.
 *
 * A destination whose backend does not exist yet is still gated on the
 * permission it *will* need. Hiding it from everybody would be the easy answer
 * and the wrong one: an administrator cannot plan around a capability they have
 * never seen, and a permission that guards nothing today is exactly the
 * permission that guards it on the day the endpoint lands.
 */
import {
  Activity,
  BarChart3,
  BookOpen,
  Boxes,
  Box,
  Building2,
  CalendarClock,
  ClipboardList,
  Clock,
  Contact,
  CreditCard,
  KeyRound,
  FileText,
  FolderTree,
  Home,
  Layers,
  LayoutDashboard,
  type LucideIcon,
  Mail,
  MailOpen,
  Megaphone,
  MessagesSquare,
  Monitor,
  Network,
  Plug,
  Radar,
  ScrollText,
  Send,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Store,
  Ticket,
  Users,
  UserPlus,
  UsersRound,
  Workflow,
  Zap,
} from 'lucide-react'

import type { Permission } from '../../lib/authz'

/** Every tint on this screen resolves to a design token. The reference art uses
 *  six hues; these are the six the token set already defines, so a rebrand or a
 *  dark-mode pass stays one file rather than an audit of eight cards. */
export type Tone = 'primary' | 'success' | 'warning' | 'critical' | 'info' | 'violet' | 'neutral'

export const TONE_TILE: Record<Tone, string> = {
  primary: 'bg-primary/10 text-primary',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/15 text-warning',
  critical: 'bg-critical/10 text-critical',
  info: 'bg-info/10 text-info',
  violet: 'bg-chart-6/10 text-chart-6',
  neutral: 'bg-muted text-muted-foreground',
}

/** What a destination can honestly say for itself when the API is not there.
 *
 * Four questions, in the order an administrator asks them: can I do anything
 * here at all, what is stopping the rest, what would have to be built, and what
 * do I get when it is. A screen that answers those is a roadmap entry; one that
 * says "no data" is a bug report.
 */
export interface Capability {
  /** One sentence. The interface is ready; name the half that is not. */
  summary: string
  /** What genuinely works today. Never invented — an empty list is allowed. */
  available: string[]
  /** The backend capability that is absent. */
  missing: string[]
  /** The endpoint(s) this screen needs, named as they would be routed. */
  api: string[]
  /** What the administrator will be able to do once the API lands. */
  unlocks: string[]
}

export interface AdminItem {
  slug: string
  label: string
  icon: LucideIcon
  /** Where the row goes. Defaults to the placeholder route for this slug. */
  to?: string
  /** True when a real screen serves it today. */
  backed?: boolean
  /** The reference marks one entry "New"; kept as data rather than a special case. */
  badge?: string
  /** Any one of these reveals the row. Empty means "follows the console" — the
   *  row is shown whenever anything else in Administration is. */
  requires: Permission[]
  /** Evaluate `requires` at **platform** scope instead of the organisation in
   *  scope. For the rows whose subject is the platform itself and not a tenant:
   *  System Health reads one broker, one worker fleet and one shared mailbox,
   *  and its endpoint refuses `settings.view` held only in an organisation. The
   *  rail has to ask the same question or it advertises a 403. */
  platformScoped?: boolean
  /** Shown on the destination when `backed` is not set. */
  capability?: Capability
  /** Rail-only. The service desk sidebar already has this destination. */
  deskHidden?: boolean
}

export interface AdminGroup {
  heading: string
  /** The group's row in the service desk sidebar, where groups are collapsible. */
  icon: LucideIcon
  items: AdminItem[]
}

/** Resolve a row to its destination. One rule, applied everywhere. */
export function hrefOf(item: AdminItem): string {
  return item.to ?? `/agent/administration/${item.slug}`
}

/* ------------------------------------------------------------------- rail */

export const ADMIN_NAV: AdminGroup[] = [
  {
    heading: 'Administration',
    icon: Settings,
    items: [
      {
        slug: 'overview',
        label: 'Overview',
        icon: Home,
        to: '/agent/administration',
        backed: true,
        requires: [],
      },
      {
        slug: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/agent',
        backed: true,
        requires: ['report.view'],
        deskHidden: true,
      },
      {
        slug: 'announcements',
        label: 'Announcements',
        icon: Megaphone,
        requires: ['settings.manage'],
        capability: {
          summary:
            'Announcement authoring is designed but has no store — nothing in the backend records a broadcast notice.',
          available: [],
          missing: [
            'An announcement model: body, audience, publish window, acknowledgement',
            'A portal surface to render one, and a console banner for the desk',
          ],
          api: ['GET/POST /api/agent/announcements/', 'GET /api/portal/announcements/'],
          unlocks: [
            'Publish a maintenance or outage notice to the portal and the console',
            'Schedule a notice ahead of a change window and expire it automatically',
          ],
        },
      },
      {
        slug: 'system-health',
        label: 'System Health',
        icon: Activity,
        to: '/agent/system-health',
        backed: true,
        // Held at platform scope, matching `CanReadPlatformHealth` on the
        // server. An Organization Admin holds `settings.view` in their tenant
        // and is refused here, so the row must not appear for them.
        platformScoped: true,
        requires: ['settings.view'],
      },
    ],
  },
  {
    heading: 'User & Access',
    icon: Users,
    items: [
      {
        slug: 'users',
        label: 'Users',
        icon: Users,
        to: '/agent/administration/users',
        backed: true,
        requires: ['user.view'],
      },
      {
        // Pointed at the settings-coverage inventory until the roles API
        // existed — a different screen about a different thing, which made the
        // rail claim roles were administered here when they were not. There is
        // now `GET /api/agent/roles/` and a screen over it.
        slug: 'roles',
        label: 'Roles & Permissions',
        icon: ShieldCheck,
        to: '/agent/administration/roles',
        backed: true,
        requires: ['role.view'],
      },
      {
        slug: 'teams',
        label: 'Teams',
        icon: UsersRound,
        to: '/agent/administration/teams',
        backed: true,
        requires: ['team.view'],
      },
      {
        slug: 'organizations',
        label: 'Organizations',
        icon: Building2,
        to: '/agent/administration/organizations',
        requires: ['organization.read'],
        capability: {
          summary:
            'Organizations and their login domains are administered here. What remains unmodelled is the structure below an organisation.',
          available: [
            'List, create, rename and deactivate organisations — rename needs organization.update in that organisation, the rest platform.manage',
            'Login domains: list, add, activate/deactivate and remove, checked by core.entra on every sign-in',
            'Per-organisation configuration is already editable under Platform → System Settings',
          ],
          missing: [
            'Departments, business units and locations have no model at all',
          ],
          api: ['Department, BusinessUnit and Location models — none exists'],
          unlocks: [
            'Attach departments and locations that Reports, Assets and routing all want',
          ],
        },
      },
      {
        slug: 'contacts',
        label: 'Contacts',
        icon: Contact,
        to: '/agent/administration/contacts',
        backed: true,
        requires: ['user.view'],
      },
      {
        slug: 'groups',
        label: 'Groups',
        icon: Users,
        requires: ['user.view'],
        capability: {
          summary:
            'There is no product Groups model, and this is a decision rather than a gap. Teams are the desk’s grouping; Django’s auth groups are not authorization here.',
          available: [
            'Teams are the real grouping and are fully administered under Administration → Teams',
            'Authorization is roles and permissions from the registry — a test asserts no permission class ever reads a Django group or is_superuser',
            'Entra group claims arrive on the sign-in token but are deliberately not persisted or mapped',
          ],
          missing: [
            'A product group distinct from a team — nothing in the schema models one',
            'A mapping from an Entra security group to a role assignment, so access could follow the directory',
          ],
          api: ['None. Reviving Django groups as authorization is explicitly rejected.'],
          unlocks: [
            'Grant a role to an Entra security group instead of person by person',
            'Deprovision access by removing somebody from a directory group',
          ],
        },
      },
    ],
  },
  {
    heading: 'Service Management',
    icon: Sparkles,
    items: [
      {
        // Both rows pointed at `/agent/catalog`, which is the *inventory* of
        // what a catalog editor would need — a roadmap page — so the rail
        // claimed services were administered here and they were not. The
        // models always existed; the API (agent/catalog_api.py) and this
        // screen are what closed it. Both rows share the screen because a
        // service without its items is a name and an item without its service
        // is unplaceable; the destination shows the pair.
        slug: 'services',
        label: 'Services',
        icon: Boxes,
        to: '/agent/administration/services',
        backed: true,
        // `catalog.manage`, not `catalog.view`. The floor role holds
        // `catalog.view` — every end user can browse the catalog — so gating
        // the rail on it would reveal the whole Administration section to an
        // Approver. The screen reads for anybody the endpoint serves; the rail
        // is for people who administer.
        requires: ['catalog.manage'],
      },
      {
        slug: 'service-categories',
        label: 'Service Categories',
        icon: Layers,
        to: '/agent/administration/service-categories',
        backed: true,
        requires: ['catalog.manage'],
      },
      {
        slug: 'slas',
        label: 'SLAs',
        icon: Clock,
        to: '/agent/administration/slas',
        backed: true,
        // Visibility follows the existing decision the nav tests pin: desk
        // configuration is an organization_admin surface. The API itself
        // reads at settings.view and writes only at platform.manage; the
        // screen disables writes it cannot make.
        requires: ['settings.manage'],
      },
      {
        slug: 'priority-rules',
        label: 'Priority Rules',
        icon: SlidersHorizontal,
        to: '/agent/administration/priority-rules',
        backed: true,
        // The old capability note claimed "the matrix is code, not data" —
        // false since the beginning: PriorityRule is a migration-seeded table,
        // and the screen now edits it.
        requires: ['settings.manage'],
      },
      {
        slug: 'business-hours',
        label: 'Business Hours',
        icon: Clock,
        to: '/agent/administration/business-hours',
        backed: true,
        requires: ['settings.manage'],
      },
      {
        slug: 'ticket-settings',
        label: 'Ticket Settings',
        icon: Ticket,
        requires: ['settings.manage'],
        capability: {
          summary:
            'The reopen window is a per-tenant setting. Status vocabularies remain in code.',
          available: [
            'tickets.reopen_days — how long a requester may reopen a closed ticket — is per organisation, under Platform → System Settings',
            'Priority is already a data-backed matrix, under Priority Rules',
            'SLA thresholds, ticket notification switches and closed-ticket retention each have their own screen',
          ],
          missing: [
            'Status and type vocabularies are choices in code, not per-tenant data — changing that is a data migration across SLA, approvals, mail and the portal, not a field',
            'There is no ticket state machine, so allowed transitions cannot be configured',
          ],
          api: ['GET/PATCH /api/agent/settings/'],
          unlocks: [
            'Rename a status or retire one without a release',
            'Refuse a close until a resolution code is given',
          ],
        },
      },
      {
        slug: 'ticket-forms',
        label: 'Ticket Forms',
        icon: ClipboardList,
        // A request form belongs to the catalog item it is for — it has no
        // identity apart from that item — so this row points at the catalog
        // screen rather than a second screen editing the same `form_schema`
        // column. The rail entry stays, because "where do I add a field to a
        // request?" is a question people ask in these words.
        to: '/agent/administration/ticket-forms',
        backed: true,
        requires: ['catalog.manage'],
      },
    ],
  },
  {
    heading: 'Assets & CMDB',
    icon: Monitor,
    items: [
      {
        slug: 'configuration-items',
        label: 'Configuration Items',
        icon: Boxes,
        to: '/agent/assets/inventory',
        backed: true,
        requires: ['asset.edit'],
      },
      {
        slug: 'ci-relationships',
        label: 'CI Relationships',
        icon: Network,
        to: '/agent/assets',
        backed: true,
        requires: ['asset.edit'],
      },
      {
        slug: 'asset-types',
        label: 'Asset Types',
        icon: Box,
        requires: ['asset.edit'],
        capability: {
          summary: 'CI type administration is ready. The types are a fixed choice list in code.',
          available: ['Every configuration item already carries a type, and the inventory filters by it'],
          missing: [
            'A CI type model with its own attribute schema, so a server and a licence stop sharing one shape',
          ],
          api: ['GET/POST /api/agent/ci-types/', 'PATCH /api/agent/ci-types/{id}/'],
          unlocks: [
            'Add a CI type without a release',
            'Give a type its own attributes and have the inventory render them',
          ],
        },
      },
      {
        slug: 'discovery',
        label: 'Discovery',
        icon: Radar,
        to: '/agent/assets/discovery',
        backed: true,
        requires: ['asset.edit'],
      },
      {
        slug: 'asset-sources',
        label: 'Asset Sources',
        icon: Plug,
        requires: ['asset.edit'],
        capability: {
          summary: 'Source configuration is ready. No import connector exists to configure.',
          available: ['Configuration items can be created and edited through the console and the API'],
          missing: [
            'A source model — Intune, Entra device, agent-based, CSV — with a schedule and a run history',
            'Reconciliation rules deciding what a second source may overwrite',
          ],
          api: ['GET/POST /api/agent/asset-sources/', 'POST /api/agent/asset-sources/{id}/run/'],
          unlocks: [
            'Import devices from Intune on a schedule instead of by hand',
            'See when a source last ran, what it changed, and what it refused to',
          ],
        },
      },
    ],
  },
  {
    heading: 'Knowledge',
    icon: BookOpen,
    items: [
      {
        slug: 'knowledge-base',
        label: 'Knowledge Base',
        icon: BookOpen,
        to: '/agent/kb',
        backed: true,
        requires: ['knowledge.publish'],
      },
      {
        slug: 'kb-categories',
        label: 'Categories',
        icon: FolderTree,
        requires: ['knowledge.publish'],
        capability: {
          summary: 'Category administration is ready. Articles carry a category; nothing manages the set.',
          available: ['Articles are categorised, and both the console and the portal filter on it'],
          missing: ['A category model with ordering and nesting — the value is a plain string today'],
          api: ['GET/POST /api/agent/kb/categories/', 'PATCH /api/agent/kb/categories/{id}/'],
          unlocks: [
            'Rename a category once instead of on every article',
            'Order and nest categories, and hide an empty one from the portal',
          ],
        },
      },
      {
        slug: 'article-templates',
        label: 'Article Templates',
        icon: FileText,
        requires: ['knowledge.publish'],
        capability: {
          summary: 'The template library is ready. Nothing stores an article template.',
          available: ['Articles can be written and published from the console editor today'],
          missing: ['A template model, and a "new article from template" path in the editor'],
          api: ['GET/POST /api/agent/kb/templates/'],
          unlocks: [
            'Start a known-error or how-to article from a house structure',
            'Keep the shape of published articles consistent across authors',
          ],
        },
      },
    ],
  },
  {
    heading: 'Automation',
    icon: Zap,
    items: [
      {
        slug: 'workflow-designer',
        label: 'Workflow Designer',
        icon: Workflow,
        to: '/agent/automation/designer',
        backed: true,
        requires: ['workflow.edit'],
      },
      {
        slug: 'automation-rules',
        label: 'Automation Rules',
        icon: Zap,
        to: '/agent/automation',
        backed: true,
        requires: ['workflow.view'],
      },
      {
        slug: 'schedules',
        label: 'Schedules',
        icon: CalendarClock,
        requires: ['workflow.edit'],
        capability: {
          summary: 'Schedule administration is ready. Celery beat is configured in code, not data.',
          available: ['Celery beat runs the periodic jobs the deployment defines — mail delta, SLA sweeps'],
          missing: [
            'A schedule model, so a workflow can be run on a cron without a redeploy',
            'Run history: when it last fired, how long it took, what it produced',
          ],
          api: ['GET/POST /api/agent/schedules/', 'GET /api/agent/schedules/{id}/runs/'],
          unlocks: [
            'Run a workflow nightly without touching the beat configuration',
            'See a failed run and retry it from the console',
          ],
        },
      },
    ],
  },
  {
    heading: 'Channels & Communication',
    icon: MessagesSquare,
    items: [
      {
        slug: 'inbound-mail',
        label: 'Inbound Mail',
        icon: MailOpen,
        to: '/agent/email/inbound',
        backed: true,
        requires: ['email.manage'],
      },
      {
        slug: 'outbound-mail',
        label: 'Outbound Mail',
        icon: Send,
        to: '/agent/email/outbound',
        backed: true,
        requires: ['email.manage'],
      },
      {
        slug: 'notifications',
        label: 'Notifications',
        icon: Megaphone,
        // Delivered in Notification Subscriptions & Audiences. It was still an
        // unbacked capability row claiming per-person subscription had no model
        // — it has had one since that commit — and an unbacked row must name
        // what it is waiting for, which this no longer can.
        to: '/agent/administration/notifications',
        backed: true,
        requires: ['email.manage'],
      },
      {
        slug: 'email-templates',
        label: 'Email Templates',
        icon: Mail,
        to: '/agent/email/templates',
        backed: true,
        requires: ['email.manage'],
      },
      {
        slug: 'microsoft-teams',
        label: 'Microsoft Teams',
        icon: MessagesSquare,
        requires: ['email.manage'],
        capability: {
          summary:
            'The Teams channel is designed and deliberately not started — it is a module in its own right.',
          available: [
            'The tenant’s Microsoft Graph application registration already exists and is used for mail',
          ],
          missing: [
            'A Teams bot registration, and Graph permissions beyond the mail scopes',
            'A conversation model mapping a Teams thread to a ticket, so replies land on the case log',
          ],
          api: ['POST /api/integrations/teams/messages/', 'GET/POST /api/agent/teams-channels/'],
          unlocks: [
            'Raise and follow a ticket from a Teams chat',
            'Notify a channel when a change enters a freeze window',
          ],
        },
      },
    ],
  },
  {
    heading: 'Platform',
    icon: Layers,
    items: [
      {
        slug: 'audit-logs',
        label: 'Audit Logs',
        icon: ScrollText,
        to: '/agent/audit',
        backed: true,
        requires: ['audit.view'],
      },
      {
        slug: 'audit-analytics',
        label: 'Audit Analytics',
        icon: BarChart3,
        to: '/agent/audit',
        backed: true,
        requires: ['audit.view'],
      },
      {
        slug: 'compliance-center',
        label: 'Compliance Center',
        icon: ShieldCheck,
        to: '/agent/compliance',
        backed: true,
        badge: 'New',
        requires: ['compliance.view'],
      },
      {
        slug: 'system-settings',
        label: 'System Settings',
        icon: Settings,
        // Was pointed at `coverage`, the settings *inventory* — a costing
        // document — while four other rows told administrators their setting
        // was "editable under Platform → System Settings". It is now.
        to: '/agent/administration/system-settings',
        backed: true,
        requires: ['settings.view'],
      },
      {
        slug: 'integrations',
        label: 'Integrations',
        icon: Plug,
        to: '/agent/administration/integrations',
        backed: true,
        requires: ['api.manage'],
      },
      {
        slug: 'marketplace',
        label: 'Marketplace',
        icon: Store,
        requires: ['platform.manage'],
        capability: {
          summary: 'Reserved. There is no extension mechanism for a marketplace to list.',
          available: [],
          missing: ['A plugin or extension model, and an installation path with a security boundary'],
          api: ['GET /api/platform/marketplace/'],
          unlocks: ['Browse and install extensions'],
        },
      },
      {
        slug: 'subscriptions',
        label: 'Subscriptions',
        icon: CreditCard,
        to: '/agent/administration/subscriptions',
        backed: true,
        // The internal software and subscription register — what each of our
        // own companies pays for. `license.view` reads it, evaluated in the
        // caller's own scope on purpose rather than at platform scope: an
        // organisation admin sees their own company's subscriptions, and
        // `core.internal_subscription_queries` narrows the rows to them.
        // Writing is `platform.billing` and is checked on the endpoint.
        requires: ['license.view'],
      },
      {
        slug: 'license-billing',
        label: 'Entitlements',
        icon: KeyRound,
        requires: ['license.view'],
        capability: {
          summary:
            'Entitlements are real and enforced. What is missing is a screen that edits module state directly — it is done through System Settings today.',
          available: [
            'The six optional modules are per-organisation settings, resolved by core.authz on every permission check',
            'Module state is visible and editable under Platform → System Settings',
          ],
          missing: [
            'A dedicated entitlement endpoint — module state is read and written through the generic settings API',
            'A dedicated screen for module state, rather than editing the settings behind it',
          ],
          api: ['GET/PATCH /api/agent/entitlements/'],
          unlocks: [
            'Switch one module on for one organisation without moving them to another plan',
            'See which permissions a module gate is currently withholding',
          ],
        },
      },
    ],
  },
]

/** Flat index, so the placeholder route can name what the caller asked for. */
export const ADMIN_ITEMS: AdminItem[] = ADMIN_NAV.flatMap((group) => group.items)

export function findAdminItem(slug: string): AdminItem | undefined {
  return ADMIN_ITEMS.find((item) => item.slug === slug)
}

/* ------------------------------------------------------------- visibility */

/** What `visibleAdminNav` needs to know. Narrower than `Authz` on purpose: this
 *  module is pure, and a test should be able to hand it a permission set. */
export interface AdminAccess {
  can: (permission: Permission) => boolean
  /** Optional, and absent means closed. A caller that does not supply it simply
   *  never sees the platform-scoped rows — which is the safe direction, and is
   *  why this is not a required field breaking every existing caller. */
  canOnPlatform?: (permission: Permission) => boolean
}

/** Does the caller hold any of a row's permissions, in the scope that row is
 *  evaluated in? The one place the `platformScoped` distinction is applied. */
function holdsFor(access: AdminAccess, item: AdminItem): boolean {
  if (item.platformScoped) {
    return item.requires.some((permission) => access.canOnPlatform?.(permission) ?? false)
  }
  return item.requires.some(access.can)
}

/** Whether one row is the caller's to see.
 *
 *  A row with no permissions of its own — Overview — follows the console rather
 *  than being visible to everybody: `visibleAdminNav` resolves it against the
 *  rest, so the front door never appears on its own with nothing behind it.
 */
export function canSeeAdminItem(access: AdminAccess, item: AdminItem): boolean {
  if (item.requires.length === 0)
    return ADMIN_ITEMS.some((other) => other.requires.length > 0 && holdsFor(access, other))
  return holdsFor(access, item)
}

/** The rail, narrowed to what the caller holds. Groups that empty out vanish. */
export function visibleAdminNav(access: AdminAccess): AdminGroup[] {
  return ADMIN_NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => canSeeAdminItem(access, item)),
  })).filter((group) => group.items.length > 0)
}

/** Whether the ADMINISTRATION section belongs in the sidebar at all.
 *
 *  Deliberately not "is this person an admin". There is no such predicate in
 *  this product — an end user holds none of these permissions and so sees
 *  nothing, a Technician holds `user.view` and so sees the two rows it opens,
 *  and Super Admin holds everything. The section is the sum of its destinations.
 */
export function canSeeAdministration(access: AdminAccess): boolean {
  return ADMIN_ITEMS.some((item) => item.requires.length > 0 && holdsFor(access, item))
}

function itemsFor(...slugs: string[]): AdminItem[] {
  return slugs.map((slug) => {
    const item = findAdminItem(slug)
    if (!item) throw new Error(`Unknown admin slug: ${slug}`)
    return item
  })
}

/* ----------------------------------------------------------- module cards */

export interface AdminModule {
  key: string
  label: string
  description: string
  icon: LucideIcon
  tone: Tone
  items: AdminItem[]
  action: { label: string; to: string }
}

/** The eight cards below the fold. Each one re-projects a rail group, so the
 *  card and the rail can never disagree about what a module contains. */
export const ADMIN_MODULES: AdminModule[] = [
  {
    key: 'access',
    label: 'User & Access Management',
    description: 'Manage users, roles, permissions, and access control',
    icon: Users,
    tone: 'primary',
    items: itemsFor('users', 'roles', 'teams', 'organizations', 'contacts', 'groups'),
    action: { label: 'Manage Access', to: '/agent/administration/coverage' },
  },
  {
    key: 'service',
    label: 'Service Management',
    description: 'Configure services, SLAs, categories, and ticket settings',
    icon: Sparkles,
    tone: 'violet',
    items: itemsFor(
      'services',
      'service-categories',
      'slas',
      'priority-rules',
      'business-hours',
      'ticket-settings',
      'ticket-forms',
    ),
    action: { label: 'Manage Services', to: '/agent/catalog' },
  },
  {
    key: 'assets',
    label: 'Assets & CMDB',
    description: 'Manage configuration items, assets, and relationships',
    icon: Monitor,
    tone: 'success',
    items: itemsFor(
      'configuration-items',
      'ci-relationships',
      'asset-types',
      'discovery',
      'asset-sources',
    ),
    action: { label: 'Manage Assets', to: '/agent/assets' },
  },
  {
    key: 'automation',
    label: 'Automation',
    description: 'Design workflows, automation rules, and schedules',
    icon: Zap,
    tone: 'success',
    items: itemsFor('workflow-designer', 'automation-rules', 'schedules'),
    action: { label: 'Manage Automation', to: '/agent/automation' },
  },
  {
    key: 'channels',
    label: 'Channels & Communication',
    description: 'Configure email, notifications, and communication channels',
    icon: MessagesSquare,
    tone: 'primary',
    items: itemsFor(
      'inbound-mail',
      'outbound-mail',
      'notifications',
      'email-templates',
      'microsoft-teams',
    ),
    action: { label: 'Manage Channels', to: '/agent/email' },
  },
  {
    key: 'knowledge',
    label: 'Knowledge Management',
    description: 'Manage knowledge base articles and categories',
    icon: BookOpen,
    tone: 'critical',
    items: itemsFor('knowledge-base', 'kb-categories', 'article-templates'),
    action: { label: 'Manage Knowledge', to: '/agent/kb' },
  },
  {
    key: 'compliance',
    label: 'Compliance & Governance',
    description: 'Monitor compliance, audit logs, and policies',
    icon: ShieldCheck,
    tone: 'violet',
    items: itemsFor('audit-logs', 'audit-analytics', 'compliance-center'),
    action: { label: 'Manage Compliance', to: '/agent/audit' },
  },
  {
    key: 'platform',
    label: 'Platform Settings',
    description: 'Configure system settings, integrations, and licensing',
    icon: Settings,
    tone: 'primary',
    items: itemsFor('system-settings', 'integrations', 'marketplace', 'license-billing'),
    action: { label: 'Platform Settings', to: '/agent/administration/coverage' },
  },
]

/* ---------------------------------------------------------- quick actions */

export interface QuickAction {
  key: string
  label: string
  icon: LucideIcon
  tone: Tone
  to: string
  /** Same rule as a rail row: any one of these reveals the tile. */
  requires: Permission[]
}

export const QUICK_ACTIONS: QuickAction[] = [
  { key: 'create-user', label: 'Create User', icon: UserPlus, tone: 'primary', to: '/agent/administration/users', requires: ['user.invite'] },
  // "Create Role" until the roles API landed, which promised something the
  // product does not do: shipped roles are rebuilt from the registry on every
  // deploy, so nothing here creates one. Granting an existing role is the act
  // this permission actually authorises, and it is real.
  { key: 'create-role', label: 'Roles & Permissions', icon: ShieldCheck, tone: 'success', to: '/agent/administration/roles', requires: ['role.manage'] },
  { key: 'add-org', label: 'Add Organization', icon: Building2, tone: 'warning', to: '/agent/administration/organizations', requires: ['organization.update'] },
  { key: 'configure-sla', label: 'Configure SLA', icon: Clock, tone: 'violet', to: '/agent/administration/slas', requires: ['settings.manage'] },
  { key: 'create-service', label: 'Create Service', icon: Box, tone: 'critical', to: '/agent/administration/services', requires: ['catalog.manage'] },
  { key: 'new-workflow', label: 'New Workflow', icon: Workflow, tone: 'warning', to: '/agent/automation/designer', requires: ['workflow.edit'] },
  { key: 'add-ci-type', label: 'Add CI Type', icon: Boxes, tone: 'info', to: '/agent/administration/asset-types', requires: ['asset.edit'] },
  // Pointed at the settings-coverage inventory, which is the screen *about*
  // settings rather than the one that edits them. The editor has existed since
  // phase 1.
  { key: 'system-settings', label: 'System Settings', icon: Settings, tone: 'neutral', to: '/agent/administration/system-settings', requires: ['settings.view'] },
]

export function visibleQuickActions(access: AdminAccess): QuickAction[] {
  return QUICK_ACTIONS.filter((action) => action.requires.some(access.can))
}
